// Regras puras de canal e caixa do formulário de campanha (decisão 30).
//
// Duas perguntas moram aqui, e as duas nasceram do defeito medido em produção na
// conta 13 em 20/09/2026: escolher "WhatsApp" abria o formulário já com a caixa
// "ARQUIVO: WhatsApp antigo" (121, Channel::Api, sessão WAHA em FAILED)
// selecionada, embora a conta tenha a caixa oficial "Plus Energy" (269) ativa.
//
// 1) Quais caixas de um canal servem para enviar AGORA.
// 2) Com que canal o formulário abre quando a pessoa já escolheu o canal fora.
//
// Não existe coluna "arquivada" em `inboxes` (conferido em db/schema.rb): o
// prefixo "ARQUIVO:" no nome é convenção do cliente, não é dado. O critério real
// é o estado da CONEXÃO do canal, que o produto já calcula em
// `useInboxConnectionHealth.inboxConnectionStatus` (waha_status para WAHA,
// reauthorization_required para os canais de OAuth). Reaproveitar essa função,
// em vez de reescrever a regra aqui, é o que mantém o formulário e o aviso de
// "caixa desconectada" contando a mesma história.
import { inboxConnectionStatus } from 'dashboard/composables/useInboxConnectionHealth';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const CAMPAIGN_CHANNELS = ['waha', 'official', 'sms'];

export const CHANNEL_LABELS = {
  waha: 'WhatsApp por QR code',
  official: 'WhatsApp oficial',
  sms: 'SMS',
};

// Famílias de canal por rota: a rota por onde a pessoa entrou já diz o que ela
// escolheu no cartão "Nova ação de marketing". WhatsApp é uma família de dois
// canais (QR code e oficial) porque a mesma página atende os dois.
export const CHANNEL_FAMILY_BY_ROUTE = {
  campaigns_whatsapp_index: ['waha', 'official'],
  campaigns_sms_index: ['sms'],
};

// Motivo escrito quando o canal não tem nenhuma caixa utilizável. É o texto que
// aparece no cartão do canal indisponível, então fala de número e de caixa, não
// de "inbox" nem de estado técnico.
export const CHANNEL_UNAVAILABLE_REASONS = {
  waha: 'nenhum número conectado por QR code',
  official: 'nenhuma caixa oficial',
  sms: 'nenhuma caixa de SMS',
};

export const channelFamilyForRoute = routeName =>
  CHANNEL_FAMILY_BY_ROUTE[routeName] || [];

// A que canal do formulário uma caixa pertence. Espelha os getters
// `getWhatsappCampaignInboxes` e `getSmsShortcodeInboxes` do store.
export const channelOfInbox = inbox => {
  if (!inbox) return null;
  if (inbox.channel_type === INBOX_TYPES.WHATSAPP) return 'official';
  if (
    inbox.channel_type === INBOX_TYPES.API &&
    inbox.additional_attributes?.waha_session_name
  ) {
    return 'waha';
  }
  if (
    inbox.channel_type === INBOX_TYPES.SMS &&
    inbox.provider === 'shortcode'
  ) {
    return 'sms';
  }
  return null;
};

// Caixa desconectada não aparece na lista. "not_applicable" (caixa sem sinal de
// saúde, como o WhatsApp oficial manual ou a sessão WAHA que ainda não foi
// consultada) continua aparecendo: esconder por falta de informação tiraria da
// tela caixa que funciona.
export const isInboxUsableForCampaign = inbox =>
  !!inbox && inboxConnectionStatus(inbox) !== 'disconnected';

export const usableInboxesForChannel = (inboxes, channel) =>
  (inboxes || []).filter(
    inbox =>
      channelOfInbox(inbox) === channel && isInboxUsableForCampaign(inbox)
  );

// Estado de cada canal: as caixas que servem, se dá para escolher o canal e, se
// não dá, o motivo escrito.
export const channelAvailability = (inboxes, channels = CAMPAIGN_CHANNELS) =>
  channels.reduce((acc, channel) => {
    const usable = usableInboxesForChannel(inboxes, channel);
    acc[channel] = {
      inboxes: usable,
      available: usable.length > 0,
      reason: usable.length ? '' : CHANNEL_UNAVAILABLE_REASONS[channel] || '',
    };
    return acc;
  }, {});

/**
 * Como o formulário abre quando a pessoa já escolheu o canal fora.
 *
 * O passo 1 só desaparece quando não sobra NADA para perguntar nele: um único
 * canal utilizável na família e uma única caixa utilizável nesse canal. Com dois
 * canais possíveis (a conta tem QR code e oficial) ou duas caixas no mesmo
 * canal, o passo 1 continua na tela, já restrito à família: escolher sozinho
 * seria exatamente o defeito da conta 13.
 *
 * @returns {{channel: string|null, inboxId: number|null, skipChannelStep: boolean}}
 */
export const resolveChannelEntry = ({
  family = [],
  availability = {},
  fallbackChannel = null,
} = {}) => {
  const usableChannels = family.filter(
    channel => availability[channel]?.available
  );

  if (usableChannels.length !== 1) {
    return {
      channel: usableChannels[0] || family[0] || fallbackChannel,
      inboxId: null,
      skipChannelStep: false,
    };
  }

  const [channel] = usableChannels;
  const usableInboxes = availability[channel].inboxes;
  if (usableInboxes.length !== 1) {
    return { channel, inboxId: null, skipChannelStep: false };
  }

  return { channel, inboxId: usableInboxes[0].id, skipChannelStep: true };
};
